var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _HznSelect_instances, _HznSelect_emit, _HznSelect_onInvalid, _HznSelect_onKeydown, _HznSelect_handleInput, _HznSelect_handleChange, _HznSelect_handleClear, _HznSelect_showClearable_get, _HznSelect_renderPlaceholder;
// class definition
import { html, LitElement, nothing } from '@horizon/base';
import { property, query, state } from '@horizon/base/decorators';
import { classMap, ifDefined, live } from '@horizon/base/directives';
import { HasSlotController } from '@horizon/common/controllers';
import { eventEmitter } from '@horizon/common/events';
import { requiredValidator } from '@horizon/common/mixins';
import { FormControlMixin } from '@open-wc/form-control';
import { submit } from '@open-wc/form-helpers';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import 'element-internals-polyfill';
import { HznDivider } from '@horizon/divider';
import { HznIconCaretDown, HznIconClose } from '@horizon/icons/individual';
import { HznInline } from '@horizon/inline';
import { HznStack } from '@horizon/stack';
import { HznText } from '@horizon/text';
import SelectStyles from './select.css.js';
/**
 *
 * @tag hzn-select
 * @tagname hzn-select
 * @summary Define a select element for choosing a single option in a form
 *
 * @fires {CustomEvent<{}>} input - Emitted every time a an option is navigated to
 * @fires {CustomEvent<{}>} change - Emitted when the select value is changed
 * @fires {CustomEvent<{}>} clear - Emitted when the select is clearable and the clear button is clicked
 */
export class HznSelect extends FormControlMixin(ScopedElementsMixin(LitElement)) {
    constructor() {
        super(...arguments);
        _HznSelect_instances.add(this);
        /**
         * @private
         */
        _HznSelect_emit.set(this, eventEmitter(this));
        /**
         * @private
         * the internal value property
         */
        this._value = '';
        this.hasSlotController = new HasSlotController(this, '[default]', 'options');
        /**
         * @private
         */
        this.options = [];
        /**
         * Whether or not the select is compact size
         */
        this.compact = false;
        /**
         * Whether or not the select's label is hidden
         */
        this.hideLabel = false;
        /**
         * Adds a clear button when the select is populated
         */
        this.clearable = false;
        /**
         * Set whether or not the input is required to have a value
         */
        this.required = false;
        /**
         * Set the input to be disabled
         */
        this.disabled = false;
        /**
         * Set the help text for the input
         */
        this.helperText = '';
        /**
         * Hides the help text for the select
         */
        this.hideHelperText = false;
        /**
         * The select's autofocus attribute
         */
        this.autofocus = false;
        /**
         * @private
         */
        _HznSelect_onInvalid.set(this, (event) => {
            var _a;
            event.preventDefault();
            (_a = this.validationTarget) === null || _a === void 0 ? void 0 : _a.focus();
        });
    }
    static get scopedElements() {
        return {
            'hzn-stack': HznStack,
            'hzn-text': HznText,
            'hzn-inline': HznInline,
            'hzn-icon-caret-down': HznIconCaretDown,
            'hzn-icon-close': HznIconClose,
            'hzn-divider': HznDivider,
        };
    }
    static get formControlValidators() {
        return [
            requiredValidator,
            {
                message(instance) {
                    if (instance.validation && instance.validation instanceof Function) {
                        return instance.validation(instance).message;
                    }
                    return '';
                },
                isValid(instance) {
                    if (instance.validation && instance.validation instanceof Function) {
                        return instance.validation(instance).valid;
                    }
                    return true;
                },
            },
        ];
    }
    /**
     * Sets the input value
     */
    get value() {
        return this._value;
    }
    set value(newValue) {
        var _a;
        const oldValue = this.value;
        const isString = typeof newValue === 'string';
        this._value = isString ? newValue : newValue === null || newValue === void 0 ? void 0 : newValue.value;
        // check if any options match the provided value and make it selected
        if (this.options) {
            const nodelist = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('option');
            if (isString && nodelist !== undefined) {
                let isFirstValueSet = false;
                nodelist.forEach((option) => {
                    option.selected = false;
                    if (option.value !== this.value || isFirstValueSet) {
                        return;
                    }
                    option.selected = true;
                    isFirstValueSet = true;
                });
            }
            else if (nodelist !== undefined) {
                nodelist.forEach((option) => {
                    option.selected = false;
                });
                newValue.selected = true;
            }
        }
        this.requestUpdate('value', oldValue);
    }
    /**
     * Programmatically clear the select
     */
    clear() {
        if (this.innerClearButton) {
            this.innerClearButton.click();
        }
    }
    // Specify that select should always update, even if the value is empty
    /**
     * @private
     */
    shouldUpdate() {
        return true;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('invalid', __classPrivateFieldGet(this, _HznSelect_onInvalid, "f"));
        this.shadowRoot.addEventListener('slotchange', async () => {
            var _a;
            const childOptions = Array.from(this.querySelectorAll(':scope > option, :scope > optgroup'));
            this.options = childOptions.map((element) => {
                element.slot = 'options';
                return element.cloneNode(true);
            });
            await this.updateComplete;
            // set initial value and displayed option if there is one
            let isFirstValueSet = false;
            (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('option').forEach((option) => {
                option.selected = false;
                if (option.value !== this.value || isFirstValueSet) {
                    return;
                }
                option.selected = true;
                isFirstValueSet = true;
            });
            this.value = this.validationTarget.value;
            // setValue here so that we're also updating the host element validity state
            // when the value changes
            this.setValue(this.value);
            this.requestUpdate();
        });
    }
    firstUpdated() {
        // set the value again with whatever the initial value is once we
        // are guaranteed to have rendered all of the internal DOM
        // and are pretty sure that validationTarget exists
        // so that the constructor's call to setValue isn't the only one
        this.setValue(this.value);
    }
    /**
     * @private
     */
    resetFormControl() {
        this.value = '';
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('keydown', __classPrivateFieldGet(this, _HznSelect_instances, "m", _HznSelect_onKeydown));
        this.removeEventListener('invalid', __classPrivateFieldGet(this, _HznSelect_onInvalid, "f"));
    }
    /**
     * @private
     */
    get showErrors() {
        return this.showError && !this.disabled;
    }
    /**
     * @private
     */
    validationMessageCallback(message) {
        this.internalErrorMessage = message;
    }
    /**
     * @private
     */
    updated(changed) {
        if (changed.has('value')) {
            this.setValue(this.value);
        }
    }
    /** Sets focus on the input. */
    focus(options) {
        this.validationTarget.focus(options);
    }
    /** Removes focus from the input. */
    blur() {
        this.validationTarget.blur();
    }
    render() {
        const inputContainerStyle = {
            'input-container': true,
            compact: Boolean(this.compact),
            error: this.showErrors,
            'clearable-has-value': Boolean(__classPrivateFieldGet(this, _HznSelect_instances, "a", _HznSelect_showClearable_get)),
        };
        const selectClasses = {
            'flex-grow': true,
            'with-placeholder': this.placeholder !== undefined && !this.value,
        };
        return html `<hzn-stack space="xsmall">
        <label for="select" class="${this.hideLabel ? 'visually-hidden' : ''}">
          ${this.hasSlotController.test('[default]')
            ? html `<hzn-text variant="text"><slot></slot></hzn-text>`
            : html `<hzn-text variant="text">${this.displayName || this.name || 'Select Input'}</hzn-text>`}
        </label>
        <div class="${classMap(inputContainerStyle)}">
          <select
            title=""
            id="select"
            class="${classMap(selectClasses)}"
            name=${ifDefined(this.name)}
            ?disabled=${this.disabled}
            ?required=${this.required}
            .value=${live(this.value)}
            autocomplete=${ifDefined(this.autocomplete)}
            ?autofocus=${this.autofocus}
            aria-describedby="helper-text"
            aria-invalid="${this.showErrors ? 'true' : 'false'}"
            @input="${__classPrivateFieldGet(this, _HznSelect_instances, "m", _HznSelect_handleInput)}"
            @change="${__classPrivateFieldGet(this, _HznSelect_instances, "m", _HznSelect_handleChange)}"
            @keydown="${__classPrivateFieldGet(this, _HznSelect_instances, "m", _HznSelect_onKeydown)}"
          >
            ${__classPrivateFieldGet(this, _HznSelect_instances, "m", _HznSelect_renderPlaceholder).call(this)} ${this.options}
          </select>
          <div class="icon-container">
            ${__classPrivateFieldGet(this, _HznSelect_instances, "a", _HznSelect_showClearable_get)
            ? html `<button tabindex="-1" class="clear-button" @click="${__classPrivateFieldGet(this, _HznSelect_instances, "m", _HznSelect_handleClear)}">
                    <span class="visually-hidden">Clear the value</span>
                    <hzn-icon-close class="clear-icon" role="presentation"></hzn-icon-close>
                  </button>
                  <hzn-divider tone="subdued" vertical class="divider"></hzn-divider>`
            : nothing}
            <span class="caret-icon" role="presentation">
              <hzn-icon-caret-down></hzn-icon-caret-down>
            </span>
          </div>
        </div>
        ${this.helperText
            ? html `<hzn-text
              class="${this.hideHelperText ? 'visually-hidden' : ''}"
              id="helper-text"
              variant="caption"
              tone="subdued"
            >
              ${this.helperText}
            </hzn-text>`
            : nothing}
        ${this.showErrors
            ? html `<hzn-text role="alert" id="error-message" class="error-message" variant="caption" tone="critical"
              >${this.internalErrorMessage}</hzn-text
            >`
            : nothing}
      </hzn-stack>
      <slot name="options" hidden></slot>`;
    }
}
_HznSelect_emit = new WeakMap(), _HznSelect_onInvalid = new WeakMap(), _HznSelect_instances = new WeakSet(), _HznSelect_onKeydown = function _HznSelect_onKeydown(event) {
    const hasModifier = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    // Pressing enter when focused on an input should submit the form like a native input
    if (event.code === 'Enter' && !hasModifier) {
        if (this.form) {
            submit(this.form);
        }
    }
}, _HznSelect_handleInput = function _HznSelect_handleInput(event) {
    event.stopImmediatePropagation();
    event.preventDefault();
    const nativeOptions = event.target.options;
    const option = nativeOptions.item(nativeOptions.selectedIndex);
    this.value = option;
    __classPrivateFieldGet(this, _HznSelect_emit, "f").call(this, { type: 'input' });
}, _HznSelect_handleChange = function _HznSelect_handleChange(event) {
    event.stopImmediatePropagation();
    event.preventDefault();
    const nativeOptions = event.target.options;
    const option = nativeOptions.item(nativeOptions.selectedIndex);
    this.value = option;
    __classPrivateFieldGet(this, _HznSelect_emit, "f").call(this, { type: 'change' });
}, _HznSelect_handleClear = function _HznSelect_handleClear() {
    this.value = '';
    this.focus();
    __classPrivateFieldGet(this, _HznSelect_emit, "f").call(this, { type: 'clear' });
}, _HznSelect_showClearable_get = function _HznSelect_showClearable_get() {
    // only show the clear button if clearable, with a value
    // and not disabled
    return this.clearable && this.value && !this.disabled;
}, _HznSelect_renderPlaceholder = function _HznSelect_renderPlaceholder() {
    if (this.placeholder === undefined) {
        return nothing;
    }
    const hasPlaceholderValue = typeof this.placeholder === 'string' && this.placeholder !== '' && this.placeholder !== 'true';
    return html `<option ?selected="${ifDefined(!this.value ? true : '')}" disabled value="">
      ${hasPlaceholderValue
        ? this.placeholder
        : 'Select one of the following options'}
    </option>`;
};
HznSelect.styles = [SelectStyles];
__decorate([
    query('select')
], HznSelect.prototype, "innerSelect", void 0);
__decorate([
    query('.clear-button')
], HznSelect.prototype, "innerClearButton", void 0);
__decorate([
    state()
], HznSelect.prototype, "options", void 0);
__decorate([
    state()
], HznSelect.prototype, "internalErrorMessage", void 0);
__decorate([
    query('select')
], HznSelect.prototype, "validationTarget", void 0);
__decorate([
    property({ type: Boolean })
], HznSelect.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-label' })
], HznSelect.prototype, "hideLabel", void 0);
__decorate([
    property({ type: Boolean })
], HznSelect.prototype, "clearable", void 0);
__decorate([
    property()
], HznSelect.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'display-name' })
], HznSelect.prototype, "displayName", void 0);
__decorate([
    property({ attribute: false })
], HznSelect.prototype, "errorMessages", void 0);
__decorate([
    property({ type: String, attribute: false })
], HznSelect.prototype, "validation", void 0);
__decorate([
    property()
], HznSelect.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true })
], HznSelect.prototype, "value", null);
__decorate([
    property({ type: Boolean })
], HznSelect.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HznSelect.prototype, "disabled", void 0);
__decorate([
    property({ type: String, attribute: 'helper-text' })
], HznSelect.prototype, "helperText", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-helper-text' })
], HznSelect.prototype, "hideHelperText", void 0);
__decorate([
    property()
], HznSelect.prototype, "autocomplete", void 0);
__decorate([
    property({ type: Boolean })
], HznSelect.prototype, "autofocus", void 0);
//# sourceMappingURL=select.js.map